import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { format, startOfMonth, endOfMonth, subDays, startOfDay, endOfDay, startOfWeek, endOfWeek, subWeeks, subMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ArrowLeft, DollarSign, FileSpreadsheet, TrendingUp, Download, CalendarIcon, Wallet, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import { useSalesReport, useSalesFiltersOptions, SalesReportFilters, PaymentStatus, ContractStatus, CardStatus, DealStatus, AutoChargeStatus } from "@/hooks/useSalesReport";
import { BusinessUnitSelector, BusinessUnit } from "@/components/finance/BusinessUnitSelector";
import { useSalesReportColumns } from "@/hooks/useSalesReportColumns";
import { SalesReportTable } from "@/components/reports/SalesReportTable";
import { supabase } from "@/integrations/supabase/client";
import { ColumnManager } from "@/components/reports/ColumnManager";

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Hoje' },
  { value: 'yesterday', label: 'Ontem' },
  { value: 'this_week', label: 'Esta semana' },
  { value: 'last_week', label: 'Semana passada' },
  { value: 'this_month', label: 'Este mês' },
  { value: 'last_month', label: 'Mês passado' },
  { value: 'last_30_days', label: 'Últimos 30 dias' },
  { value: 'custom', label: 'Selecionar período' },
];

const getDateRange = (period: string, customRange?: { from?: Date; to?: Date }) => {
  const today = new Date();
  switch (period) {
    case 'today':
      return { start: startOfDay(today), end: endOfDay(today) };
    case 'yesterday':
      const yesterday = subDays(today, 1);
      return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
    case 'this_week':
      return { start: startOfWeek(today, { weekStartsOn: 1 }), end: endOfDay(today) };
    case 'last_week':
      const lastWeek = subWeeks(today, 1);
      return { start: startOfWeek(lastWeek, { weekStartsOn: 1 }), end: endOfWeek(lastWeek, { weekStartsOn: 1 }) };
    case 'this_month':
      return { start: startOfMonth(today), end: endOfDay(today) };
    case 'last_month':
      const lastMonth = subMonths(today, 1);
      return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
    case 'last_30_days':
      return { start: subDays(today, 30), end: endOfDay(today) };
    case 'custom':
      return { 
        start: customRange?.from || subDays(today, 30), 
        end: customRange?.to || today 
      };
    default:
      return { start: startOfMonth(today), end: endOfMonth(today) };
  }
};

export default function SalesReport() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [periodFilter, setPeriodFilter] = useState('this_month');
  const [customDateRange, setCustomDateRange] = useState<{ from?: Date; to?: Date }>();
  const [filters, setFilters] = useState<SalesReportFilters>(() => {
    const range = getDateRange('this_month');
    return { startDate: range.start, endDate: range.end };
  });

  // Sincronizar período com filtros
  useEffect(() => {
    const range = getDateRange(periodFilter, customDateRange);
    setFilters(f => ({ ...f, startDate: range.start, endDate: range.end }));
  }, [periodFilter, customDateRange]);

  const { data, isLoading, error } = useSalesReport(filters);
  const { sources, channels, consultants, isLoading: filtersLoading } = useSalesFiltersOptions();

  // Realtime subscription para deals e payments
  useEffect(() => {
    const channel = supabase
      .channel('sales-report-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'deals' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['sales-report'] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'payments' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['sales-report'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
  
  const {
    columns,
    visibleColumns,
    columnOrder,
    sortConfig,
    orderedVisibleColumns,
    toggleColumnVisibility,
    reorderColumns,
    toggleSort,
    resetToDefault,
  } = useSalesReportColumns();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(i18n.language === "pt-BR" ? "pt-BR" : "en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleExportCSV = () => {
    if (!data?.deals.length) return;

    // Use only visible columns in the order they appear
    const headers = orderedVisibleColumns.map(col => t(col.labelKey));

    const rows = data.deals.map((deal) => 
      orderedVisibleColumns.map(col => {
        // Exportar valor efetivo para coluna total_amount
        if (col.id === "total_amount") return deal.effective_amount;
        const value = (deal as any)[col.accessor];
        if (col.id === "closed_date") return value || "";
        return value || "-";
      })
    );

    // Escape CSV values properly
    const escapeCSV = (value: any) => {
      const str = String(value);
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvContent = [
      headers.map(escapeCSV).join(","),
      ...rows.map((row) => row.map(escapeCSV).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `sales-report-${format(filters.startDate, "yyyy-MM-dd")}-${format(filters.endDate, "yyyy-MM-dd")}.csv`;
    link.click();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/reports")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">{t("salesReport.title")}</h1>
              <p className="text-muted-foreground">{t("salesReport.subtitle")}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ColumnManager
            columns={columns}
            columnOrder={columnOrder}
            visibleColumns={visibleColumns}
            onToggleVisibility={toggleColumnVisibility}
            onReorder={reorderColumns}
            onReset={resetToDefault}
          />
          <Button onClick={handleExportCSV} disabled={!data?.deals.length}>
            <Download className="h-4 w-4 mr-2" />
            {t("salesReport.exportCSV")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="w-[200px]">
              <label className="text-sm font-medium mb-2 block">{t("salesReport.period")}</label>
              <Select value={periodFilter} onValueChange={setPeriodFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {periodFilter === 'custom' && (
              <div className="w-[280px]">
                <label className="text-sm font-medium mb-2 block">Intervalo</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      {customDateRange?.from ? (
                        customDateRange.to ? (
                          `${format(customDateRange.from, 'dd/MM/yyyy')} - ${format(customDateRange.to, 'dd/MM/yyyy')}`
                        ) : format(customDateRange.from, 'dd/MM/yyyy')
                      ) : 'Selecione as datas'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      selected={customDateRange}
                      onSelect={setCustomDateRange}
                      numberOfMonths={2}
                      locale={ptBR}
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            <div className="w-[200px]">
              <label className="text-sm font-medium mb-2 block">{t("salesReport.businessUnit", "Unidade de Negócio")}</label>
              <BusinessUnitSelector
                value={(filters.businessUnit as BusinessUnit) || 'all'}
                onChange={(value) => setFilters((f) => ({ ...f, businessUnit: value }))}
                includeAll={true}
              />
            </div>

            <div className="w-[200px]">
              <label className="text-sm font-medium mb-2 block">{t("salesReport.seller")}</label>
              <MultiSelect
                options={consultants.map((c) => ({
                  value: c.id,
                  label: `${c.first_name} ${c.last_name}`.trim(),
                }))}
                value={filters.salesConsultantIds || []}
                onChange={(values) => setFilters((f) => ({ ...f, salesConsultantIds: values.length ? values : undefined }))}
                placeholder={t("salesReport.allSellers")}
              />
            </div>

            <div className="w-[200px]">
              <label className="text-sm font-medium mb-2 block">{t("salesReport.source")}</label>
              <MultiSelect
                options={sources.map((s) => ({ value: s, label: s }))}
                value={filters.sources || []}
                onChange={(values) => setFilters((f) => ({ ...f, sources: values.length ? values : undefined }))}
                placeholder={t("salesReport.allSources")}
              />
            </div>

            <div className="w-[200px]">
              <label className="text-sm font-medium mb-2 block">{t("salesReport.channel")}</label>
              <MultiSelect
                options={channels.map((c) => ({ value: c, label: c }))}
                value={filters.channels || []}
                onChange={(values) => setFilters((f) => ({ ...f, channels: values.length ? values : undefined }))}
                placeholder={t("salesReport.allChannels")}
              />
            </div>

            <div className="w-[200px]">
              <label className="text-sm font-medium mb-2 block">{t("salesReport.filterPaymentStatus")}</label>
              <MultiSelect
                options={[
                  { value: "settled", label: t("salesReport.paymentStatus.settled") },
                  { value: "paid", label: t("salesReport.paymentStatus.paid") },
                  { value: "partial", label: t("salesReport.paymentStatus.partial") },
                  { value: "late", label: t("salesReport.paymentStatus.late") },
                  { value: "cancelled", label: t("salesReport.paymentStatus.cancelled") },
                  { value: "no_payment", label: t("salesReport.paymentStatus.noPayment") },
                ]}
                value={filters.paymentStatuses || []}
                onChange={(values) => setFilters((f) => ({ ...f, paymentStatuses: values.length ? values as PaymentStatus[] : undefined }))}
                placeholder={t("salesReport.allPaymentStatus")}
              />
            </div>

            <div className="w-[200px]">
              <label className="text-sm font-medium mb-2 block">{t("salesReport.filterContractStatus")}</label>
              <MultiSelect
                options={[
                  { value: "draft", label: t("salesReport.contractStatus.draft") },
                  { value: "sent", label: t("salesReport.contractStatus.sent") },
                  { value: "completed", label: t("salesReport.contractStatus.completed") },
                  { value: "issues", label: t("salesReport.contractStatus.issues") },
                  { value: "no_contract", label: t("salesReport.contractStatus.noContract") },
                ]}
                value={filters.contractStatuses || []}
                onChange={(values) => setFilters((f) => ({ ...f, contractStatuses: values.length ? values as ContractStatus[] : undefined }))}
                placeholder={t("salesReport.allContractStatus")}
              />
            </div>

            <div className="w-[200px]">
              <label className="text-sm font-medium mb-2 block">{t("salesReport.filterCardStatus")}</label>
              <MultiSelect
                options={[
                  { value: "saved", label: t("salesReport.cardStatus.saved") },
                  { value: "not_saved", label: t("salesReport.cardStatus.notSaved") },
                ]}
                value={filters.cardStatuses || []}
                onChange={(values) => setFilters((f) => ({ ...f, cardStatuses: values.length ? values as CardStatus[] : undefined }))}
                placeholder={t("salesReport.allCardStatus")}
              />
            </div>

            <div className="w-[200px]">
              <label className="text-sm font-medium mb-2 block">{t("salesReport.filterDealStatus")}</label>
              <MultiSelect
                options={[
                  { value: "active", label: t("salesReport.dealStatus.active") },
                  { value: "cancelled", label: t("salesReport.dealStatus.cancelled") },
                ]}
                value={filters.dealStatuses || []}
                onChange={(values) => setFilters((f) => ({ ...f, dealStatuses: values.length ? values as DealStatus[] : undefined }))}
                placeholder={t("salesReport.allDealStatus")}
              />
            </div>

            <div className="w-[200px]">
              <label className="text-sm font-medium mb-2 block">{t("salesReport.filterAutoChargeStatus")}</label>
              <MultiSelect
                options={[
                  { value: "enabled", label: t("salesReport.autoChargeStatus.enabled") },
                  { value: "card_only", label: t("salesReport.autoChargeStatus.cardOnly") },
                  { value: "no_card", label: t("salesReport.autoChargeStatus.noCard") },
                ]}
                value={filters.autoChargeStatuses || []}
                onChange={(values) => setFilters((f) => ({ ...f, autoChargeStatuses: values.length ? values as AutoChargeStatus[] : undefined }))}
                placeholder={t("salesReport.allAutoChargeStatus")}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4">
          <Card className="min-h-[100px]">
            <CardContent className="pt-6 h-full">
              <div className="flex items-center gap-3 h-full">
                <div className="p-2 bg-primary/10 rounded-lg shrink-0">
                  <DollarSign className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">{t("salesReport.totalSales")}</p>
                  <p className="font-bold whitespace-nowrap tabular-nums leading-none tracking-tight text-[clamp(1rem,1.25vw,1.75rem)]">{formatCurrency(data.summary.totalSales)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="min-h-[100px]">
            <CardContent className="pt-6 h-full">
              <div className="flex items-center gap-3 h-full">
                <div className="p-2 bg-green-500/10 rounded-lg shrink-0">
                  <Wallet className="h-5 w-5 text-green-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">{t("salesReport.totalReceived")}</p>
                  <p className="font-bold whitespace-nowrap tabular-nums leading-none tracking-tight text-[clamp(1rem,1.25vw,1.75rem)]">{formatCurrency(data.summary.totalReceived)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="min-h-[100px]">
            <CardContent className="pt-6 h-full">
              <div className="flex items-center gap-3 h-full">
                <div className="p-2 bg-sky-500/10 rounded-lg shrink-0">
                  <CalendarClock className="h-5 w-5 text-sky-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">{t("salesReport.totalScheduled")}</p>
                  <p className="font-bold whitespace-nowrap tabular-nums leading-none tracking-tight text-[clamp(1rem,1.25vw,1.75rem)]">{formatCurrency(data.summary.totalScheduled)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="min-h-[100px]">
            <CardContent className="pt-6 h-full">
              <div className="flex items-center gap-3 h-full">
                <div className="p-2 bg-emerald-500/10 rounded-lg shrink-0">
                  <FileSpreadsheet className="h-5 w-5 text-emerald-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">{t("salesReport.dealsCount")}</p>
                  <p className="font-bold whitespace-nowrap tabular-nums leading-none tracking-tight text-[clamp(1rem,1.25vw,1.75rem)]">{data.summary.dealsCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="min-h-[100px]">
            <CardContent className="pt-6 h-full">
              <div className="flex items-center gap-3 h-full">
                <div className="p-2 bg-blue-500/10 rounded-lg shrink-0">
                  <TrendingUp className="h-5 w-5 text-blue-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">{t("salesReport.averageTicket")}</p>
                  <p className="font-bold whitespace-nowrap tabular-nums leading-none tracking-tight text-[clamp(1rem,1.25vw,1.75rem)]">{formatCurrency(data.summary.averageTicket)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="pt-6">
          <SalesReportTable
            deals={data?.deals || []}
            columns={orderedVisibleColumns}
            sortConfig={sortConfig}
            onSort={toggleSort}
            isLoading={isLoading}
            error={error}
          />
        </CardContent>
      </Card>
    </div>
  );
}
